import PouchDB from 'pouchdb';
import PouchDBFind from 'pouchdb-find';
import type { DatabaseAdapter, PouchDBInstance, PouchError } from '../types';
import type { BasePouchDocument } from '../types';

PouchDB.plugin(PouchDBFind);

export abstract class BasePouchDBAdapter<T extends { _id: string, type: string, version: number }> implements DatabaseAdapter<T> {
  protected db: PouchDBInstance;
  private readonly docType: string;

  constructor(dbName: string, docType: string) {
    this.db = new PouchDB(dbName);
    this.docType = docType;
  }

  protected abstract fromDocument(doc: BasePouchDocument): T;

  private unwrapPouchDocument(doc: BasePouchDocument): T {
    return this.fromDocument(doc);
  }

  async findById(id: string): Promise<T | null> {
    try {
      const doc = await this.db.get(id);
      const result = this.unwrapPouchDocument(doc);
      return result;
    } catch (error) {
      if ((error as PouchError).status === 404) {
        return null;
      }
      throw this.handleError(error as PouchError);
    }
  }

  async findAll(): Promise<T[]> {
    try {
      console.log('Finding all documents of type:', this.docType);
      // Use allDocs + client-side filter: db.find() with a single 'type' field
      // may return empty results when no matching compound index exists (PouchDB gotcha).
      const result = await this.db.allDocs({ include_docs: true });
      return result.rows
        .filter(row => (row.doc as any)?.type === this.docType)
        .map(row => this.unwrapPouchDocument(row.doc!));
    } catch (error) {
      throw this.handleError(error as PouchError);
    }
  }

  async query(selector: Record<string, any>): Promise<T[]> {
    console.log('Querying documents of type:', this.docType, 'with selector:', selector);
    try {
      // If selector is empty (only type), use allDocs for reliability
      // PouchDB's find() may not resolve indexes correctly for single-field queries
      if (Object.keys(selector).length === 0) {
        const result = await this.db.allDocs({ include_docs: true });
        return result.rows
          .filter(row => (row.doc as any).type === this.docType)
          .map(row => this.unwrapPouchDocument(row.doc!));
      }

      // For queries with additional fields, use find() with indexes
      const result = await this.db.find({
        selector: {
          type: this.docType,
          ...Object.entries(selector).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: value
          }), {})
        }
      });
      return result.docs.map(doc => this.unwrapPouchDocument(doc));
    } catch (error) {
      throw this.handleError(error as PouchError);
    }
  }

  async createIndex(fields: string[]): Promise<void> {
    try {
      await this.db.createIndex({
        index: {
          fields: ['type', ...fields]
        }
      });
    } catch (error) {
      throw this.handleError(error as PouchError);
    }
  }

  // async bulkCreate(entities: T[]): Promise<T[]> {
  //   try {
  //     const docs = entities.map(entity => this.wrapDocument(entity));
  //     const results = await this.db.bulkDocs(docs);
  //     return entities.map((entity, index) => ({
  //       ...entity,
  //       id: results[index].id
  //     })) as T[];
  //   } catch (error) {
  //     throw this.handleError(error as PouchError);
  //   }
  // }

  // async bulkUpdate(entities: T[]): Promise<T[]> {
  //   try {
  //     const docs = await Promise.all(
  //       entities.map(async (entity) => {
  //         const existing = await this.db.get(entity.id) as PouchDBDocumentType<T>;
  //         const doc = this.wrapDocument(entity);
  //         return {
  //           ...doc,
  //           _rev: existing._rev
  //         };
  //       })
  //     );

  //     const results = await this.db.bulkDocs(docs);
  //     return entities.map((entity, index) => ({
  //       ...entity,
  //       id: results[index].id
  //     })) as T[];
  //   } catch (error) {
  //     throw this.handleError(error as PouchError);
  //   }
  // }

  // async create(entity: T): Promise<T> {
  //   try {
  //     const doc = this.wrapDocument(entity);
  //     const result = await this.db.put(doc);
  //     return {
  //       ...entity,
  //       id: result.id
  //     } as T;
  //   } catch (error) {
  //     throw this.handleError(error as PouchError);
  //   }
  // }

  // async update(entity: T): Promise<T> {
  //   try {
  //     const existing = await this.db.get(entity.id) as PouchDBDocumentType<T>;
  //     const doc = this.wrapDocument(entity);
  //     const result = await this.db.put({
  //       ...doc,
  //       _rev: existing._rev
  //     });
  //     return {
  //       ...entity,
  //       id: result.id
  //     } as T;
  //   } catch (error) {
  //     throw this.handleError(error as PouchError);
  //   }
  // }

  // async delete(id: string): Promise<void> {
  //   try {
  //     const doc = await this.db.get(id) as PouchDBDocumentType<T>;
  //     await this.db.remove(doc);
  //   } catch (error) {
  //     if ((error as PouchError).status !== 404) {
  //       throw this.handleError(error as PouchError);
  //     }
  //   }
  // }

  protected handleError(error: PouchError): Error {
    switch (error.status) {
      case 404:
        return new Error(`Document not found: ${error.reason}`);
      case 409:
        return new Error(`Conflict updating document: ${error.reason}`);
      default:
        return new Error(`Database error: ${error.reason || error.message}`);
    }
  }
}